import { useState } from 'react';
import { niceScale } from './chartScale';

interface StackedBarChartProps {
  data: { label: string; male: number; female: number }[];
}

// Figma "Audience Fit — Chart" (node 1295:36184): plot bars scale to a 204px
// column, a y-axis with a "nice" max, dashed gridlines, the total % above each
// bar, and a purple(male)/pink(female) split separated by a 2px white line.
const SCALE_H = 204; // px height that maps to the axis max (0 → bottom)
const MALE_COLOR = '#9647f8'; // Brand/400
const FEMALE_COLOR = '#e0508a';

interface Hovered { label: string; sex: 'male' | 'female' }

export function StackedBarChart({ data }: StackedBarChartProps) {
  // Segment-level (one bar's one color at a time), not whole-bar — matches
  // the donut's single-segment highlight pattern.
  const [hovered, setHovered] = useState<Hovered | null>(null);
  const fmtPct = (n: number) => `${(Math.round(n * 100) / 100).toFixed(2)}%`;

  const maxTotal = Math.max(...data.map((d) => d.male + d.female), 0);
  const { axisMax, ticks } = niceScale(maxTotal);

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex gap-2.5" style={{ height: SCALE_H }}>
        {/* Y-axis */}
        <div className="relative w-9 shrink-0">
          {ticks.map((t) => (
            <span
              key={t}
              className="absolute right-0 -translate-y-1/2 text-caption-regular/4 font-medium text-[#737373]"
              style={{ top: `${(1 - t / axisMax) * 100}%` }}
            >
              {t}
            </span>
          ))}
        </div>

        {/* Chart — bars sit flush on the 0 gridline; the x-axis label row
            below is a sibling, not a flex child in here, so it can't push
            the bars' baseline off zero. */}
        <div className="relative flex-1">
          {/* Dashed gridlines */}
          {ticks.map((t) => (
            <div
              key={t}
              className="absolute inset-x-0 border-t border-dashed border-[#e5e5e5]"
              style={{ top: `${(1 - t / axisMax) * 100}%` }}
            />
          ))}

          {/* Bands overlaid on the gridlines */}
          <div className="absolute inset-0 flex items-end justify-between gap-2">
            {data.map((d) => {
            const total = d.male + d.female;
            const barH = (total / axisMax) * SCALE_H;
            const femH = total > 0 ? (d.female / total) * barH : 0;
            const maleH = barH - femH;
            const isMaleHovered = hovered?.label === d.label && hovered.sex === 'male';
            const isFemaleHovered = hovered?.label === d.label && hovered.sex === 'female';
            const maleOpacity = hovered == null || isMaleHovered ? 1 : 0.3;
            const femaleOpacity = hovered == null || isFemaleHovered ? 1 : 0.3;
            return (
              <div key={d.label} className="relative flex h-full flex-1 flex-col items-center justify-end">
                {/* Total % above the bar */}
                <span className="mb-1 text-[13px] font-bold leading-none text-[#171717]">
                  {Math.round(total)}%
                </span>
                {/* Split bar: purple (male, top) over pink (female, bottom) —
                    each half is independently hoverable, like a donut segment.
                    Each segment owns its tooltip (only that segment's own
                    label — the age bracket lives on the x-axis instead, so
                    it isn't repeated in the tooltip). The color fill is
                    clipped to rounded corners in its own layer; the hover
                    zones + tooltips live in a sibling, unclipped layer —
                    otherwise the male (top) segment's tooltip, which opens
                    upward past the bar's own top edge, gets cut off by the
                    fill layer's overflow-hidden. */}
                <div className="relative w-full max-w-[128px]" style={{ height: `${barH}px` }}>
                  <div className="absolute inset-0 overflow-hidden rounded-[4px]">
                    <div style={{ height: `${maleH}px`, background: MALE_COLOR, opacity: maleOpacity, transition: 'opacity 0.15s ease' }} />
                    <div
                      style={{
                        height: `${femH}px`,
                        background: FEMALE_COLOR,
                        opacity: femaleOpacity,
                        borderTop: maleH > 0 && femH > 0 ? '2px solid #fff' : 'none',
                        transition: 'opacity 0.15s ease',
                      }}
                    />
                  </div>
                  <div
                    className="absolute inset-x-0 top-0"
                    onMouseEnter={() => setHovered({ label: d.label, sex: 'male' })}
                    onMouseLeave={() => setHovered(null)}
                    style={{ height: `${maleH}px`, cursor: 'pointer' }}
                  >
                    {isMaleHovered && (
                      <div className="absolute bottom-full left-1/2 z-10 mb-1 -translate-x-1/2 whitespace-nowrap rounded-lg bg-stone-950 px-2 py-1.5 text-[10px] font-medium leading-[1.4] text-white">
                        <div>Male</div>
                        <div>{fmtPct(d.male)}</div>
                      </div>
                    )}
                  </div>
                  <div
                    className="absolute inset-x-0"
                    onMouseEnter={() => setHovered({ label: d.label, sex: 'female' })}
                    onMouseLeave={() => setHovered(null)}
                    style={{ top: `${maleH}px`, height: `${femH}px`, cursor: 'pointer' }}
                  >
                    {isFemaleHovered && (
                      <div className="absolute bottom-full left-1/2 z-10 mb-1 -translate-x-1/2 whitespace-nowrap rounded-lg bg-stone-950 px-2 py-1.5 text-[10px] font-medium leading-[1.4] text-white">
                        <div>Female</div>
                        <div>{fmtPct(d.female)}</div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
            })}
          </div>
        </div>
      </div>

      {/* X-axis: age bracket per bar, laid out as a sibling row so it
          can't affect the bars' 0-baseline above. */}
      <div className="flex gap-2.5">
        <div className="w-9 shrink-0" />
        <div className="flex flex-1 justify-between gap-2">
          {data.map((d) => (
            <span
              key={d.label}
              className="flex-1 truncate text-center text-[11px] font-medium text-muted-foreground"
            >
              {d.label}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
